import { useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { connectSocket, disconnectSocket } from './socket';

/* =========================================================
   VISITOR LOG
   ========================================================= */

import {
  preEndCheckReceived,
  visitorLogSocketUpdateReceived,
  type PreEndCheckPayload,
  type VisitorLogSocketUpdatePayload,
} from '../Redux/slices/visitorLogSlice';

/* =========================================================
   RED ZONE
   ========================================================= */

import {
  redZoneSocketUpdateReceived,
  type RedZoneSocketUpdatePayload,
} from '../Redux/slices/redZoneSlice';

/* =========================================================
   INCIDENT REPORT
   ========================================================= */

import {
  incidentReportSocketUpdateReceived,
  type IncidentReportSocketUpdatePayload,
} from '../Redux/slices/incidentReportSlice';

/* =========================================================
   DELIVERY
   ========================================================= */

import {
  deliverySocketUpdateReceived,
  type DeliverySocketUpdatePayload,
} from '../Redux/slices/deliverySlice';

/* =========================================================
   COMPONENT
   ========================================================= */

export function RealtimeSocketBridge() {
  const dispatch = useDispatch();

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    let isMounted = true;

    async function initSocketBridge() {
      const socket = await connectSocket();

      if (!isMounted) {
        return;
      }

      /* =================================================
           VISITOR LOG UPDATE
           ================================================= */

      const handleVisitorLogUpdate = (
        payload: VisitorLogSocketUpdatePayload
      ) => {
        console.log('✅ visitorLog:update received in bridge:', payload);

        dispatch(visitorLogSocketUpdateReceived(payload));
      };

      /* =================================================
           PRE-END CHECK
           ================================================= */

      const handlePreEndCheck = (payload: PreEndCheckPayload) => {
        console.log('⏰ visitorLog:preEndCheck received in bridge:', payload);

        dispatch(preEndCheckReceived(payload));
      };

      /* =================================================
           RED ZONE
           ================================================= */

      const handleRedZoneUpdate = (payload: RedZoneSocketUpdatePayload) => {
        console.log('🗺️ redZone:update received in web bridge:', payload);

        dispatch(redZoneSocketUpdateReceived(payload));
      };

      /* =================================================
           INCIDENT REPORT
           ================================================= */

      const handleIncidentReportUpdate = (
        payload: IncidentReportSocketUpdatePayload
      ) => {
        console.log(
          '🚨 incidentReport:update received in web bridge:',
          payload
        );

        dispatch(incidentReportSocketUpdateReceived(payload));
      };

      /* =================================================
           DELIVERY
           ================================================= */

      const handleDeliveryUpdate = (payload: DeliverySocketUpdatePayload) => {
        console.log('📦 delivery:update received in web bridge:', payload);

        dispatch(deliverySocketUpdateReceived(payload));
      };

      /* =================================================
           REMOVE EXISTING LISTENERS
           =================================================
           
           This protects against duplicate handlers if the
           bridge reconnects or React remounts the component.
           ================================================= */

      socket.off('visitorLog:update', handleVisitorLogUpdate);

      socket.off('visitorLog:preEndCheck', handlePreEndCheck);

      /*
       * Keep legacy lowercase event listener because
       * existing backend/client code may still emit it.
       */
      socket.off('visitorlog:preEndCheck', handlePreEndCheck);

      socket.off('redZone:update', handleRedZoneUpdate);

      socket.off('incidentReport:update', handleIncidentReportUpdate);

      socket.off('delivery:update', handleDeliveryUpdate);

      /* =================================================
           REGISTER LISTENERS
           ================================================= */

      socket.on('visitorLog:update', handleVisitorLogUpdate);

      socket.on('visitorLog:preEndCheck', handlePreEndCheck);

      socket.on('visitorlog:preEndCheck', handlePreEndCheck);

      socket.on('redZone:update', handleRedZoneUpdate);

      socket.on('incidentReport:update', handleIncidentReportUpdate);

      socket.on('delivery:update', handleDeliveryUpdate);

      /* =================================================
           CLEANUP
           ================================================= */

      cleanup = () => {
        socket.off('visitorLog:update', handleVisitorLogUpdate);

        socket.off('visitorLog:preEndCheck', handlePreEndCheck);

        socket.off('visitorlog:preEndCheck', handlePreEndCheck);

        socket.off('redZone:update', handleRedZoneUpdate);

        socket.off('incidentReport:update', handleIncidentReportUpdate);

        socket.off('delivery:update', handleDeliveryUpdate);

        disconnectSocket();
      };
    }

    void initSocketBridge();

    return () => {
      isMounted = false;

      cleanup?.();
    };
  }, [dispatch]);

  return null;
}
